import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import type { AppUsagePageRow, AppUsagePageDetail, UsageWindow, UsageAppScope } from '../../types/appUsage';
import { getAppUsagePageDetail } from '../../services/appUsageApi';
import { Sparkline } from '../powerbi-usage/Sparkline';
import { fmtNum, fmtDateTime, lastUsedBadgeClass, daysSince } from './format';

interface Props {
    pages: AppUsagePageRow[];
    window: UsageWindow;
    app: UsageAppScope;
}

type SortKey = 'appName' | 'pageName' | 'views' | 'apiCalls' | 'uniqueUsers' | 'adoptionPct' | 'lastUsedAt';

/** A path is unique only within its app: /settings exists in the Reporting App and in sales-app. */
const rowKey = (p: AppUsagePageRow) => `${p.app}|${p.pageUrl}`;

export function AppUsagePagesTab({ pages, window, app }: Props) {
    const showApp = app === 'all';
    const [sortBy, setSortBy] = useState<SortKey>('views');
    const [sortAsc, setSortAsc] = useState(true); // least-used first — the point of the page
    const [selected, setSelected] = useState<string | null>(null);
    const [detail, setDetail] = useState<AppUsagePageDetail | null>(null);
    const [detailError, setDetailError] = useState<string | null>(null);

    useEffect(() => {
        setDetail(null);
        setDetailError(null);
        const row = pages.find(p => rowKey(p) === selected);
        if (!row) return;
        let cancelled = false;
        getAppUsagePageDetail(row.pageUrl, window, row.app)
            .then(d => { if (!cancelled) setDetail(d); })
            .catch(err => { if (!cancelled) setDetailError(err instanceof Error ? err.message : 'Failed to load'); });
        return () => { cancelled = true; };
    }, [selected, window, pages]);

    const sorted = [...pages].sort((a, b) => {
        const dir = sortAsc ? 1 : -1;
        switch (sortBy) {
            case 'appName': return (a.appName.localeCompare(b.appName) || a.pageName.localeCompare(b.pageName)) * dir;
            case 'pageName': return a.pageName.localeCompare(b.pageName) * dir;
            case 'views': return (a.views - b.views) * dir;
            case 'apiCalls': return (a.apiCalls - b.apiCalls) * dir;
            case 'uniqueUsers': return (a.uniqueUsers - b.uniqueUsers) * dir;
            case 'adoptionPct': return ((a.adoptionPct ?? -1) - (b.adoptionPct ?? -1)) * dir;
            case 'lastUsedAt': {
                const av = a.lastUsedAt ? Date.parse(a.lastUsedAt) : 0;
                const bv = b.lastUsedAt ? Date.parse(b.lastUsedAt) : 0;
                return (av - bv) * dir;
            }
        }
    });

    const selectedRow = pages.find(p => rowKey(p) === selected) ?? null;

    const header = (key: SortKey, label: string, right = false) => (
        <th
            className={`cursor-pointer select-none px-3 py-2 text-xs font-semibold text-gray-500 hover:text-gray-800 ${right ? 'text-right' : 'text-left'}`}
            onClick={() => {
                if (sortBy === key) setSortAsc(!sortAsc);
                else { setSortBy(key); setSortAsc(key === 'pageName' || key === 'appName'); }
            }}
        >
            {label}{sortBy === key ? (sortAsc ? ' ▲' : ' ▼') : ''}
        </th>
    );

    return (
        <div className="flex gap-3">
            <div className={`overflow-x-auto rounded-lg border border-gray-200 bg-white ${selected ? 'w-[55%]' : 'w-full'}`}>
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                        <tr>
                            {showApp && header('appName', 'App')}
                            {header('pageName', 'Page')}
                            {header('views', 'Views', true)}
                            {header('apiCalls', 'API calls', true)}
                            {header('uniqueUsers', 'Users', true)}
                            {header('adoptionPct', 'Adoption', true)}
                            {header('lastUsedAt', 'Last used')}
                            {!selected && <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Trend</th>}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {sorted.map(p => {
                            const days = daysSince(p.lastUsedAt);
                            const key = rowKey(p);
                            return (
                                <tr
                                    key={key}
                                    onClick={() => setSelected(selected === key ? null : key)}
                                    className={`cursor-pointer hover:bg-gray-50 ${selected === key ? 'bg-blue-50' : ''}`}
                                >
                                    {showApp && <td className="whitespace-nowrap px-3 py-2 text-gray-600">{p.appName}</td>}
                                    <td className="px-3 py-2">
                                        <span className="font-medium text-gray-900">{p.pageName}</span>
                                        {p.pageName !== p.pageUrl && (
                                            <span className="ml-2 hidden text-xs text-gray-400 xl:inline">{p.pageUrl}</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums">{fmtNum(p.views)}</td>
                                    <td className="px-3 py-2 text-right tabular-nums">{fmtNum(p.apiCalls)}</td>
                                    <td className="px-3 py-2 text-right tabular-nums">
                                        {fmtNum(p.uniqueUsers)}
                                        {p.entitledUsers > 0 && <span className="text-gray-400"> / {fmtNum(p.entitledUsers)}</span>}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums">
                                        {p.adoptionPct === null ? '—' : `${p.adoptionPct}%`}
                                    </td>
                                    <td className="px-3 py-2">
                                        <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${lastUsedBadgeClass(p.lastUsedAt)}`}>
                                            {p.lastUsedAt ? (days === 0 ? 'today' : `${days}d ago`) : 'Never'}
                                        </span>
                                    </td>
                                    {!selected && <td className="px-3 py-2"><Sparkline values={p.trend} /></td>}
                                </tr>
                            );
                        })}
                        {sorted.length === 0 && (
                            <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-500">No pages.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {selected && (
                <div className="w-[45%] overflow-y-auto rounded-lg border border-gray-200 bg-white p-4" style={{ maxHeight: '70vh' }}>
                    <div className="mb-3 flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-gray-900">
                            {selectedRow?.pageName ?? selected}
                            <span className="ml-2 text-xs font-normal text-gray-400">
                                {selectedRow && selectedRow.pageName !== selectedRow.pageUrl ? `${selectedRow.pageUrl} · ` : ''}
                                {selectedRow?.appName}
                            </span>
                        </h3>
                        <button type="button" onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-700" aria-label="Close">
                            <X className="h-4 w-4" />
                        </button>
                    </div>

                    {detailError && <p className="text-sm text-red-600">{detailError}</p>}
                    {!detail && !detailError && <p className="text-sm text-gray-500">Loading…</p>}

                    {detail && (
                        <div className="space-y-4">
                            <div>
                                <h4 className="mb-1 text-xs font-semibold uppercase text-gray-500">
                                    Daily activity ({window === 'all' ? 'last 90 days' : `last ${window} days`}) — UTC
                                </h4>
                                <div className="h-40">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={detail.dailySeries}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                                            <XAxis dataKey="day" tickFormatter={(d: string) => d.slice(5)} interval="preserveStartEnd" tick={{ fontSize: 11 }} />
                                            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                                            <Tooltip />
                                            <Legend wrapperStyle={{ fontSize: 11 }} />
                                            <Bar dataKey="views" name="Views" stackId="a" fill="#3b82f6" />
                                            <Bar dataKey="apiCalls" name="API calls" stackId="a" fill="#93c5fd" radius={[2, 2, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            <div>
                                <h4 className="mb-1 text-xs font-semibold uppercase text-gray-500">Top users (window)</h4>
                                <ul className="space-y-0.5 text-sm">
                                    {detail.topUsers.map(u => (
                                        <li key={u.email} className="flex justify-between gap-2">
                                            <span className="truncate text-gray-800">{u.email}</span>
                                            <span className="whitespace-nowrap tabular-nums text-gray-500">{fmtNum(u.events)} events</span>
                                        </li>
                                    ))}
                                    {detail.topUsers.length === 0 && <li className="text-gray-500">No activity in window.</li>}
                                </ul>
                            </div>

                            {detail.endpoints.length > 0 && (
                                <div>
                                    <h4 className="mb-1 text-xs font-semibold uppercase text-gray-500">Endpoints (window)</h4>
                                    <ul className="space-y-0.5 text-xs">
                                        {detail.endpoints.map(e => (
                                            <li key={e.endpoint} className="flex justify-between gap-2">
                                                <span className="truncate font-mono text-gray-700">{e.endpoint}</span>
                                                <span className="whitespace-nowrap tabular-nums text-gray-500">
                                                    {fmtNum(e.calls)} · {fmtNum(e.uniqueUsers)} users
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {detail.features.length > 0 && (
                                <div>
                                    <h4 className="mb-1 text-xs font-semibold uppercase text-gray-500">Feature events (window)</h4>
                                    <ul className="space-y-0.5 text-xs">
                                        {detail.features.map(f => (
                                            <li key={f.feature} className="flex justify-between gap-2">
                                                <span className="truncate font-mono text-gray-700">{f.feature}</span>
                                                <span className="whitespace-nowrap tabular-nums text-gray-500">
                                                    {fmtNum(f.count)} · {fmtNum(f.uniqueUsers)} users
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {detail.inactiveEntitledUsers.length > 0 && (
                                <div>
                                    <h4 className="mb-1 text-xs font-semibold uppercase text-gray-500">
                                        Entitled but inactive in window ({detail.inactiveEntitledUsers.length})
                                    </h4>
                                    <p className="text-xs leading-5 text-gray-600">{detail.inactiveEntitledUsers.join(', ')}</p>
                                </div>
                            )}

                            <div>
                                <h4 className="mb-1 text-xs font-semibold uppercase text-gray-500">Recent events (UTC)</h4>
                                <div className="max-h-48 overflow-y-auto">
                                    <table className="min-w-full text-xs">
                                        <tbody className="divide-y divide-gray-100">
                                            {detail.recentEvents.map((e, i) => (
                                                <tr key={i}>
                                                    <td className="whitespace-nowrap py-1 pr-2 text-gray-500">{fmtDateTime(e.at)}</td>
                                                    <td className="py-1 pr-2 text-gray-700">{e.type}</td>
                                                    <td className="truncate py-1 pr-2 text-gray-700">{e.user}</td>
                                                    <td className="truncate py-1 font-mono text-gray-500">{e.endpoint ?? e.feature ?? ''}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
